import React from 'react';
import type { Game } from '../../../types/game';
import { CountdownBoxes } from '../gameParts';
import { Label, Panel } from '../primitives';
import { kickOff, perPlayerCost, shortDate, shortDateTime } from './dates';

interface Props {
  game: Game;
  weather: string | null;
  /** Called when the visible countdown hits zero, so the page can refetch. */
  onCountdownDone?: () => void;
}

/** The next deadline the hero counts down to, by game phase (same rules as the classic GameHeader). */
export function nextDeadline(game: Game, now = new Date()): { label: string; at: string } | null {
  const future = (iso?: string | null) => !!iso && new Date(iso) > now;
  if (game.status === 'upcoming' && future(game.registration_window_start)) {
    return { label: 'Registration opens in', at: game.registration_window_start };
  }
  if (game.status === 'open' && future(game.registration_window_end)) {
    return { label: 'Registration closes in', at: game.registration_window_end };
  }
  if (game.status === 'players_announced' && future(game.team_announcement_time)) {
    return { label: 'Teams announced in', at: game.team_announcement_time as string };
  }
  return null;
}

/**
 * Game hero panel: match tag, date and kick-off (kick-off in gold), venue line,
 * and a HRS/MIN/SEC countdown to the next deadline.
 */
export const GameHero: React.FC<Props> = ({ game, weather, onCountdownDone }) => {
  const deadline = nextDeadline(game);
  const cost = perPlayerCost(game.pitch_cost, game.max_players);
  const perSide = game.max_players ? Math.floor(game.max_players / 2) : null;
  const venueBits = [
    game.venue?.name,
    perSide ? `${perSide}-a-side` : null,
    cost ? `${cost} each` : null,
  ].filter(Boolean);

  return (
    <Panel tone="raised" className="flex flex-col gap-3.5 !rounded-[18px] p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="rounded bg-ut-acc px-[9px] py-1 font-ut-display text-xs font-bold tracking-[0.16em] text-ut-acc-ink">
          MATCH #{game.sequence_number}
        </span>
        {weather && <span className="text-right text-xs font-semibold text-ut-mut">{weather}</span>}
      </div>
      <div className="font-ut-display text-[46px] font-extrabold uppercase leading-[0.9]">
        {shortDate(game.date)}
        <br />
        <span className="text-ut-acc">{kickOff(game.date)}</span>
      </div>
      <div className="text-sm font-medium text-ut-mut">
        {venueBits.join(' · ')}
        {game.venue?.google_maps_url && (
          <>
            {' · '}
            <a href={game.venue.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-ut-acc hover:text-ut-ink">
              Map
            </a>
          </>
        )}
      </div>
      {deadline && (
        <div className="flex flex-col gap-2 border-t border-ut-line pt-2.5">
          <Label>{deadline.label}</Label>
          <CountdownBoxes target={deadline.at} onDone={onCountdownDone} />
          <div className="text-xs text-ut-mut">
            {game.registration_window_end &&
              `Registration ${new Date(game.registration_window_end) > new Date() ? 'closes' : 'closed'} ${shortDateTime(game.registration_window_end)}`}
            {game.team_announcement_time && ` · teams announced ${shortDateTime(game.team_announcement_time)}`}
          </div>
        </div>
      )}
    </Panel>
  );
};
